import * as React from 'react';

import { useCallback, useEffect, useLayoutEffect, useState } from 'react';

export const WINDOW: Window | undefined =
  (typeof window === 'object' && window) || undefined;

type BreakpointType = {
  xmobile: number;
  mobile: number;
  tablet: number;
  desktop: number;
  widescreen: number;
  hd: number;
};

type BreakpointResults = {
  [key in keyof BreakpointType]: boolean;
};

export const defaultBreakpoints: BreakpointType = {
  xmobile: 323,
  mobile: 619,
  tablet: 991,
  desktop: 992,
  widescreen: 1280,
  hd: 1920
};

/**
 * Device Factory helper methods
 */
export class Devices {
  static breakpoints = defaultBreakpoints;

  static withSizes(breakpoints: BreakpointType): Devices {
    this.breakpoints = Object.assign(this.breakpoints, breakpoints);
    return this;
  }

  /**
   * is width xmobile and below
   */
  static isXMobile(): boolean {
    const width = WINDOW?.innerWidth || 1000;
    return width <= this.breakpoints.xmobile;
  }

  /**
   * is width mobile and below. returns true even on xmobile
   */
  static isMobile(): boolean {
    const width = WINDOW?.innerWidth || 1000;
    return width <= this.breakpoints.mobile;
  }

  /**
   * is width above mobile and below desktop
   */
  static isTabletOnly(): boolean {
    const width = WINDOW?.innerWidth || 1000;
    return width <= this.breakpoints.tablet && width > this.breakpoints.mobile;
  }

  /**
   * is width less than tablet? returns true even on mobile and xmobile
   */
  static isTablet(): boolean {
    const width = WINDOW?.innerWidth || 1000;
    return width <= this.breakpoints.tablet;
  }

  /**
   * is width greater than tablet?
   */
  static isDesktop(): boolean {
    const width = WINDOW?.innerWidth || 1000;
    return width >= this.breakpoints.tablet;
  }

  /**
   * is width greater than max-flex of desktop (1280px). i.e the breakpoint after which
   * max-width does not keep growing.
   */
  static isWidescreen(): boolean {
    const width = WINDOW?.innerWidth || 0;
    return width >= this.breakpoints.widescreen;
  }

  /**
   * is width greater than or equal to HD.
   */
  static isHD(): boolean {
    const width = WINDOW?.innerWidth || 0;
    return width >= this.breakpoints.hd;
  }

  /**
   * Re-renders functional component at Breakpoints from defaultBreakpoints.
   * @returns ```
   {
        xmobile: boolean,
        mobile: boolean,
        tablet: boolean,
        desktop: boolean,
        widescreen: boolean,
        hd: boolean
    }
   ```
   * [xmobile < mobile < tablet] [desktop > widescreen > HD]
   * tablet will be true even when xmobile and mobile are true.
   * desktop will be true even when widescreen and hd are true.
   *
   *
   * Breakpoints can be overridden by calling withSizes() first.
   * e.g. Devices.withSizes(mySize).useMedia()
   */
  static useMedia(): BreakpointResults {
    // Array containing a media query list for each query
    const queries = [
      `(max-width: ${this.breakpoints.xmobile}px)`,
      `(max-width: ${this.breakpoints.mobile}px)`,
      `(max-width: ${this.breakpoints.tablet}px)`,
      `(min-width: ${this.breakpoints.desktop}px)`,
      `(min-width: ${this.breakpoints.widescreen}px)`,
      `(min-width: ${this.breakpoints.hd}px)`
    ];
    // init as [0,1,2,3,4]
    // const values = [...Array(queries.length)].map((_e, i) => i);

    const mediaQueryLists = queries.map(
      (q) => WINDOW?.matchMedia && WINDOW.matchMedia(q)
    );

    // Function that gets value based on matching media query
    const getValue = (): BreakpointResults => {
      // Get index of first media query that matches
      const matchedResults = mediaQueryLists.map((mql) => mql?.matches);
      const valObj: BreakpointResults = {
        xmobile: false,
        mobile: false,
        tablet: false,
        desktop: false,
        widescreen: false,
        hd: false
      };
      Object.keys(this.breakpoints).forEach((key, i) => {
        valObj[key] = matchedResults[i] as boolean;
      });
      return valObj as BreakpointResults;
    };

    // State and setter for matched value
    const [value, setValue] = useState<BreakpointResults>(getValue);

    useEffect(
      () => {
        // Event listener callback
        // Note: By defining getValue outside of useEffect we ensure that it has ...
        // ... current values of hook args (as this hook callback is created once on mount).
        const handler = () => setValue(getValue);
        // Set a listener for each media query with above handler as callback.
        mediaQueryLists.forEach((mql) => mql?.addListener(handler));
        // Remove listeners on cleanup
        return () =>
          mediaQueryLists.forEach((mql) => mql?.removeListener(handler));
      },
      [] // Empty array ensures effect is only run on mount and unmount
    );

    return value;
  }
}
// -------------------------------------------------------------

// copied from typescript/lib/dom. issues exporting types directly
interface ResizeObserverSize {
  readonly blockSize: number;
  readonly inlineSize: number;
}

interface ResizeObserverEntry {
  readonly borderBoxSize: ReadonlyArray<ResizeObserverSize>;
  readonly contentBoxSize: ReadonlyArray<ResizeObserverSize>;
  readonly contentRect: DOMRectReadOnly;
  readonly target: Element;
}

type UseResizeObserverFunctionType = (
  ref?: React.RefObject<HTMLElement>,
  callback?: (entry: DOMRectReadOnly) => void
) => void;

export const useResizeObserver: UseResizeObserverFunctionType = (
  ref,
  callback
) => {
  if (!WINDOW || !(WINDOW as any)?.ResizeObserver) return;
  const handleResize: (entries: ResizeObserverEntry[]) => void = useCallback(
    (entries) => {
      if (!Array.isArray(entries)) {
        return;
      }
      if (callback) {
        callback(entries[0].contentRect);
      }
    },
    [callback]
  );

  useLayoutEffect(() => {
    if (!ref?.current) {
      return;
    }
    const ResizeObserverWindow = (WINDOW as any).ResizeObserver;
    const observer = new ResizeObserverWindow(handleResize);
    observer.observe(ref.current);
    return () => {
      observer.disconnect();
    };
  }, [ref?.current]);
};

export const useLockBodyScroll = (): {
  setLock: React.Dispatch<React.SetStateAction<boolean>>;
} => {
  const [lock, setLock] = useState<boolean>(false);

  useEffect(() => {
    if (!WINDOW) return;
    const originalOverflow: string | undefined = WINDOW.getComputedStyle(
      document.body
    ).overflow;
    if (lock) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      if (originalOverflow) {
        document.body.style.overflow = originalOverflow;
      }
    };
  }, [lock]);

  return {
    setLock
  };
};
/**
 * @param callback the function to be called when there's a change in the attributes provided
 * @param targetElement the element is to be observed
 * @param attributesFilter specific attributes to observe
 */
export const useMutationObserver = (
  callback: () => void,
  attributesFilter: string[],
  targetElement?: React.RefObject<HTMLElement>
): void => {
  useEffect(() => {
    // Observer a target element for changes in the attributes provided in the attributes filter
    const observer = new MutationObserver(callback);
    if (targetElement && targetElement.current) {
      observer.observe(targetElement.current, {
        attributes: true,
        attributeFilter: attributesFilter
      });
    }
    return () => observer.disconnect();
  }, []);
};
